import {
  CREDIT_PRICE,
  CreateEventSession,
  EventSessionStatus,
  EventSessionWithDetail,
  EventWithDetail,
  TicketTierTemplate,
  TicketTierType,
  UserRank
} from "@garther/interfaces";
import { useEventsInvalidation } from "@garther/query-hooks";
import { Box, Button, Input, Popup, Select, TiptapEditor } from "@garther/ui/components";
import { toast, useI18n } from "@garther/ui/libs";
import { useForm } from "@tanstack/react-form";
import { useRouter } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { useCreateSession, useTicketTierTemplatesQuery } from "@/features/event/hooks/use-events-query";

interface CreateSessionFormProps {
  event: EventWithDetail;
  isOpen: boolean;
  onClose: () => void;
  initialData: EventSessionWithDetail | null;
}

interface TicketTierFormData {
  type: TicketTierType;
  title: string;
  description: string;
  cap: number;
  price: number;
  templateId?: string;

  maxQtyPerOrder: number;
  minQtyPerOrder: number;
}

interface FormData {
  startAt: string;
  endAt?: string;
  cap: number;
  ticketTiers: TicketTierFormData[];
}

const ticketTierTypeOptions = [
  { value: TicketTierType.NORMAL, label: "Normal" },
  { value: TicketTierType.GROUP, label: "Group" },
  { value: TicketTierType.GROUP_WOMEN, label: "Group - Women" },
  { value: TicketTierType.GROUP_MEN, label: "Group - Men" },
  { value: TicketTierType.GROUP_WITH_FRIEND, label: "Group with Friends" }
];

export function CreateSessionForm({ event, initialData, isOpen, onClose }: CreateSessionFormProps) {
  const { t, formatCredit } = useI18n();
  const router = useRouter();
  const invalidate = useEventsInvalidation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templatesEnabled, setTemplatesEnabled] = useState(false);
  const { mutateAsync: createSession } = useCreateSession();
  const { data: ticketTemplatesData, isLoading: loadingTemplates } = useTicketTierTemplatesQuery(event.id, {
    enabled: templatesEnabled
  });
  const ticketTemplates: TicketTierTemplate[] = ticketTemplatesData ?? [];

  const form = useForm<FormData>({
    defaultValues: {
      startAt: "",
      endAt: "",
      cap: initialData?.cap ?? 0,
      ticketTiers: initialData?.ticketTiers.map((tier) => ({
        ...tier,
        price: tier.price * CREDIT_PRICE
      })) ?? [
        {
          type: TicketTierType.NORMAL,
          title: "",
          description: "",
          cap: 0,
          price: 0,

          minQtyPerOrder: 1,
          maxQtyPerOrder: 10
        }
      ]
    },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        // Validate start date
        if (!value.startAt.trim()) {
          toast.error(t("common.validation.required"));
          return;
        }

        // Calculate total ticket cap
        const totalTicketCap = value.ticketTiers.reduce((sum, tier) => sum + tier.cap, 0);
        const sessionCap = value.cap > 0 ? Math.min(value.cap, totalTicketCap) : totalTicketCap;

        if (sessionCap === 0) {
          toast.error("Please set ticket tiers capacity");
          return;
        }

        const sessionData: CreateEventSession = {
          status: EventSessionStatus.AVAILABLE,
          startAt: new Date(value.startAt),
          endAt: value.endAt ? new Date(value.endAt) : null,
          cap: sessionCap,
          ticketTiers: value.ticketTiers.map((tier) => ({
            type: tier.type,
            title: tier.title.trim(),
            description: tier.description.trim(),
            cap: tier.cap,
            price: Math.ceil(tier.price / CREDIT_PRICE),
            minQtyPerOrder: tier.minQtyPerOrder,
            maxQtyPerOrder: tier.maxQtyPerOrder,
            minRankRequired: UserRank.NONE
          }))
        };

        await createSession({ eventId: event.id, data: sessionData });
        toast.success("Session created successfully");

        // Invalidate and refetch event data
        invalidate.invalidateEvent(event.id);
        router.invalidate();

        onClose();
        form.reset();
      } catch (error) {
        console.error(error);
        toast.error(t("common.error.tryAgain"));
      } finally {
        setIsSubmitting(false);
      }
    }
  });

  const handleLoadTemplates = () => {
    if (ticketTemplates.length > 0) return;
    setTemplatesEnabled(true);
  };

  const handleApplyTemplate = (templateId: string, tierIndex: number) => {
    const template = ticketTemplates.find((t) => t.id === templateId);
    if (!template) return;

    // Get current form state
    const currentState = form.state.values;
    const newTiers = [...currentState.ticketTiers];
    newTiers[tierIndex] = {
      type: template.type,
      title: template.title,
      description: template.description,
      cap: template.cap,
      price: template.price * CREDIT_PRICE,
      templateId: template.id,
      minQtyPerOrder: template.minQtyPerOrder,
      maxQtyPerOrder: template.maxQtyPerOrder
    };

    // Update using form API
    form.setFieldValue("ticketTiers", newTiers);
  };

  return (
    <Popup isOpen={isOpen} onClose={onClose} className="max-w-2xl">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <div className="space-y-6">
          <div>
            <h2 className="text-responsive-lg font-medium text-text-primary">{t("eventSession.create")}</h2>
          </div>

          <div className="space-y-6">
            {/* Start Date */}
            <form.Field
              name="startAt"
              validators={{
                onChange: ({ value }) => (!value?.trim() ? "Start date is required" : undefined)
              }}
            >
              {(field) => (
                <Input
                  id="startAt"
                  type="datetime-local"
                  label={t("eventSession.startAt")}
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.value)}
                  required
                  disabled={isSubmitting}
                  error={field.state.meta.errors[0] || undefined}
                />
              )}
            </form.Field>

            {/* End Date */}
            <form.Field name="endAt">
              {(field) => (
                <Input
                  id="endAt"
                  type="datetime-local"
                  label={t("eventSession.endAt")}
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isSubmitting}
                  error={field.state.meta.errors[0] || undefined}
                />
              )}
            </form.Field>

            {/* Capacity */}
            <form.Field name="cap">
              {(field) => (
                <Input
                  type="number"
                  label={t("eventSession.capacity")}
                  value={field.state.value || ""}
                  onChange={(e) => field.handleChange(Number(e.target.value))}
                  placeholder="0"
                  disabled={isSubmitting}
                />
              )}
            </form.Field>

            {/* Ticket Tiers */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-responsive-md font-medium text-text-primary">Ticket Tiers</h3>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleLoadTemplates}
                  disabled={loadingTemplates || isSubmitting}
                  className="text-responsive-sm"
                >
                  {loadingTemplates ? "Loading..." : "Load Templates"}
                </Button>
              </div>

              <form.Field name="ticketTiers" mode="array">
                {(field) => (
                  <div className="space-y-4">
                    {field.state.value.map((_, tierIndex) => (
                      <Box key={tierIndex} className="p-4 space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-responsive-sm font-medium text-text-primary">
                            Tier {tierIndex + 1}
                          </h4>
                          {field.state.value.length > 1 && (
                            <button
                              type="button"
                              onClick={() => field.removeValue(tierIndex)}
                              className="text-text-secondary hover:text-text-primary"
                              disabled={isSubmitting}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        {/* Template Selector */}
                        {ticketTemplates.length > 0 && (
                          <div className="space-y-2">
                            <label className="text-responsive-sm font-medium text-text-secondary">
                              Use Template (or fill manually)
                            </label>
                            <select
                              onChange={(e) => {
                                const templateId = e.target.value;
                                if (templateId) {
                                  handleApplyTemplate(templateId, tierIndex);
                                }
                              }}
                              defaultValue=""
                              className="w-full h-10 rounded-sm border border-border-light bg-transparent px-3 py-2 text-responsive-base font-normal text-text-primary focus:outline-none focus:border-border-dark disabled:cursor-not-allowed disabled:opacity-50"
                              disabled={isSubmitting}
                            >
                              <option value="">Create from scratch</option>
                              {ticketTemplates.map((t) => (
                                <option key={t.id} value={t.id}>
                                  {t.title} - {formatCredit(t.price * CREDIT_PRICE)}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        {/* Type */}
                        <form.Field
                          name={`ticketTiers[${tierIndex}].type`}
                          validators={{
                            onChange: ({ value }) => (!value ? "Type is required" : undefined)
                          }}
                        >
                          {(typeField) => (
                            <Select
                              label="Type"
                              value={typeField.state.value}
                              onChange={(e) => typeField.handleChange(e.target.value as TicketTierType)}
                              options={ticketTierTypeOptions}
                              required
                              error={typeField.state.meta.errors[0] || undefined}
                              disabled={isSubmitting}
                            />
                          )}
                        </form.Field>

                        {/* Title */}
                        <form.Field
                          name={`ticketTiers[${tierIndex}].title`}
                          validators={{
                            onChange: ({ value }) => (!value?.trim() ? "Title is required" : undefined)
                          }}
                        >
                          {(titleField) => (
                            <Input
                              label="Title"
                              value={titleField.state.value}
                              onChange={(e) => titleField.handleChange(e.target.value)}
                              placeholder="e.g., Early Bird"
                              required
                              error={titleField.state.meta.errors[0] || undefined}
                              disabled={isSubmitting}
                            />
                          )}
                        </form.Field>

                        {/* Description */}
                        <form.Field name={`ticketTiers[${tierIndex}].description`}>
                          {(field) => (
                            <TiptapEditor
                              label="Mô tả"
                              placeholder="Mô tả về loại vé này"
                              error={field.state.meta.errors[0] || undefined}
                              value={field.state.value || ""}
                              onChange={field.handleChange}
                              disabled={isSubmitting}
                            />
                          )}
                        </form.Field>

                        {/* Price and Capacity */}
                        <div className="grid grid-cols-2 gap-3">
                          <form.Field
                            name={`ticketTiers[${tierIndex}].price`}
                            validators={{
                              onChange: ({ value }) => (value < 0 ? "Price cannot be negative" : undefined)
                            }}
                          >
                            {(priceField) => (
                              <Input
                                label="Giá (VND)"
                                type="text"
                                inputMode="numeric"
                                value={
                                  priceField.state.value ? priceField.state.value.toLocaleString("vi-VN") : ""
                                }
                                onChange={(e) => {
                                  const raw = e.target.value.replace(/\./g, "");
                                  priceField.handleChange(parseFloat(raw) || 0);
                                }}
                                placeholder="0"
                                required
                                error={priceField.state.meta.errors[0] || undefined}
                                disabled={isSubmitting}
                              />
                            )}
                          </form.Field>

                          <form.Field
                            name={`ticketTiers[${tierIndex}].cap`}
                            validators={{
                              onChange: ({ value }) =>
                                value <= 0 ? "Capacity must be greater than 0" : undefined
                            }}
                          >
                            {(capField) => (
                              <Input
                                type="number"
                                label="Capacity"
                                value={capField.state.value || ""}
                                onChange={(e) => capField.handleChange(Number(e.target.value) || 0)}
                                placeholder="0"
                                required
                                error={capField.state.meta.errors[0] || undefined}
                                disabled={isSubmitting}
                              />
                            )}
                          </form.Field>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <form.Field
                            name={`ticketTiers[${tierIndex}].minQtyPerOrder`}
                            validators={{
                              onChange: ({ value }) => {
                                if (!value || value <= 0) return "Số lượng phải lớn hơn 0";
                                return undefined;
                              }
                            }}
                          >
                            {(field) => (
                              <Input
                                label="Số lượng tối thiểu trên 1 lượt mua"
                                type="number"
                                value={field.state.value.toString()}
                                onChange={(e) => field.handleChange(parseInt(e.target.value) || 0)}
                                placeholder="0"
                                required
                                error={field.state.meta.errors[0] || undefined}
                                disabled={isSubmitting}
                              />
                            )}
                          </form.Field>

                          <form.Field
                            name={`ticketTiers[${tierIndex}].maxQtyPerOrder`}
                            validators={{
                              onChange: ({ value, fieldApi }) => {
                                if (!value || value <= 0) return "Số lượng phải lớn hơn 0";
                                const minQty = fieldApi.form.getFieldValue(
                                  `ticketTiers[${tierIndex}].minQtyPerOrder`
                                );
                                if (minQty && value < minQty)
                                  return "Số lượng tối đa phải lớn hơn hoặc bằng số lượng tối thiểu";
                                return undefined;
                              }
                            }}
                          >
                            {(field) => (
                              <Input
                                label="Số lượng tối đa trên 1 lượt mua"
                                type="number"
                                value={field.state.value.toString()}
                                onChange={(e) => field.handleChange(parseFloat(e.target.value) || 0)}
                                placeholder="0"
                                required
                                error={field.state.meta.errors[0] || undefined}
                                disabled={isSubmitting}
                              />
                            )}
                          </form.Field>
                        </div>
                      </Box>
                    ))}

                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        field.pushValue({
                          type: TicketTierType.NORMAL,
                          title: "",
                          description: "",
                          cap: 0,
                          price: 0,
                          minQtyPerOrder: 1,
                          maxQtyPerOrder: 10
                        })
                      }
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Ticket Tier
                    </Button>
                  </div>
                )}
              </form.Field>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-primary">
              {isSubmitting ? "Creating..." : "Create Session"}
            </Button>
          </div>
        </div>
      </form>
    </Popup>
  );
}
